import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Permit, PermitClose, PermitIssue, PermitSuspend } from '@cloudsign/shared';
import type { PermitType } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Button, Card, CheckboxField, ConfirmButton, LinkButton, PageHeader, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { isoToLocal, localToIso, permitsKey, usePermitSites, usePermitTypes } from './api';
import { KeyValue, PermitStatusBadge, SignOffField } from './parts';
import type { SignOff } from './parts';

const unique = (roles: readonly string[]): string[] => [...new Set(roles)];

/** One permit: details, checklist, signatures, actions by status and permission, event log and PDF (permits.md s5). */
export function PermitPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: [...permitsKey, 'one', id], queryFn: () => api.get(`/permits/${id}`, { schema: Permit }) });
  const types = usePermitTypes();
  const sites = usePermitSites();
  const qc = useQueryClient();
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const p = q.data;
  const type = types.data?.find((x) => x.id === p.typeId) ?? null;
  const siteName = sites.data?.find((s) => s.id === p.siteId)?.name ?? '';
  const update = (next: Permit) => {
    qc.setQueryData([...permitsKey, 'one', id], next);
    void qc.invalidateQueries({ queryKey: [...permitsKey, 'list'] });
  };
  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {p.number}
            <PermitStatusBadge status={p.status} />
          </span>
        }
        description={`${p.typeName}${siteName ? ` · ${siteName}` : ''}`}
        actions={
          <>
            <LinkButton to="/permits">{t('p3pt.detail.back')}</LinkButton>
            {p.pdfFileId ? (
              <a
                href={`/api/v1/permits/${p.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 sm:min-h-9"
              >
                {t('p3pt.detail.pdf')}
              </a>
            ) : null}
          </>
        }
      />
      {p.status === 'suspended' && p.suspendedReason ? <Alert kind="info">{t('p3pt.detail.suspendedWhy', { reason: p.suspendedReason })}</Alert> : null}
      {p.pdfSha256 ? <p className="text-xs text-slate-600">{t('p3pt.detail.sha', { sha: p.pdfSha256 })}</p> : null}

      <Card title={t('p3pt.detail.details')}>
        <dl className="grid gap-4 sm:grid-cols-2">
          <KeyValue label={t('p3pt.field.holder')}>{p.holderName || '-'}</KeyValue>
          <KeyValue label={t('p3pt.field.company')}>{p.holderCompany || '-'}</KeyValue>
          <KeyValue label={t('p3pt.field.area')}>{p.area || '-'}</KeyValue>
          <KeyValue label={t('p3pt.detail.requestedBy')}>{p.requestedByLabel}</KeyValue>
          <KeyValue label={t('p3pt.detail.starts')}>{formatDateTime(p.startsAt) || '-'}</KeyValue>
          <KeyValue label={t('p3pt.col.ends')}>{formatDateTime(p.endsAt) || '-'}</KeyValue>
          <KeyValue label={t('p3pt.detail.issuedBy')}>{p.issuedByLabel ?? '-'}</KeyValue>
          <KeyValue label={t('p3pt.field.description')}>{p.description || '-'}</KeyValue>
          <KeyValue label={t('p3pt.field.conditions')}>{p.conditions || '-'}</KeyValue>
          {type?.precautions ? <KeyValue label={t('p3pt.field.precautions')}>{type.precautions}</KeyValue> : null}
        </dl>
      </Card>

      {p.status !== 'requested' && p.checklist.length > 0 ? (
        <Card title={t('p3pt.detail.checklist')}>
          <ul className="space-y-1.5">
            {p.checklist.map((i) => (
              <li key={i.id} className="flex items-start gap-2 text-sm">
                <span aria-hidden="true">{i.checked ? '✔' : '✘'}</span>
                <span>
                  {i.text}
                  <span className="sr-only">{i.checked ? t('p3pt.detail.ticked') : t('p3pt.detail.notTicked')}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card title={t('p3pt.detail.signatures')}>
        {p.signatures.length === 0 ? (
          <p className="text-sm text-slate-600">{t('p3pt.detail.noSignatures')}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {p.signatures.map((s, n) => (
              <li key={`${s.role}-${s.stage}-${n}`} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm">
                <span className="font-medium">{s.name}</span>
                <span className="text-slate-600">
                  {t(`p3pt.role.${s.role}`)} · {t(`p3pt.stage.${s.stage}`)} · {formatDateTime(s.signedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Actions permit={p} type={type} onChanged={update} />

      <Card title={t('p3pt.detail.events')}>
        {(p.events ?? []).length === 0 ? (
          <p className="text-sm text-slate-600">{t('p3pt.detail.noEvents')}</p>
        ) : (
          <ol className="space-y-2">
            {(p.events ?? []).map((e) => (
              <li key={e.id} className="text-sm">
                <span className="font-medium">{t(`p3pt.event.${e.kind}`)}</span>
                <span className="text-slate-600">
                  {' '}
                  · {e.actorLabel} · {formatDateTime(e.at)}
                </span>
                {e.note ? <span className="block text-slate-700">{e.note}</span> : null}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function Actions({ permit: p, type, onChanged }: { permit: Permit; type: PermitType | null; onChanged: (p: Permit) => void }) {
  const canIssue = useCan('permits.issue');
  const canClose = useCan('permits.close');
  const canRaise = useCan('permits.raise');
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const done = (text: string) => (next: Permit) => {
    onChanged(next);
    toast(text);
  };
  const cancel = useMutation({ mutationFn: () => api.post(`/permits/${p.id}/cancel`, { schema: Permit }), onSuccess: done(t('p3pt.act.cancelled')) });
  const resume = useMutation({ mutationFn: () => api.post(`/permits/${p.id}/resume`, { schema: Permit }), onSuccess: done(t('p3pt.act.resumed')) });
  const suspend = useMutation({
    mutationFn: () => api.post(`/permits/${p.id}/suspend`, { body: PermitSuspend.parse({ reason }), schema: Permit }),
    onSuccess: (next) => {
      setSuspending(false);
      setReason('');
      done(t('p3pt.act.suspended'))(next);
    },
  });
  const showIssue = p.status === 'requested' && canIssue;
  const showCancel = (p.status === 'requested' || p.status === 'issued') && canRaise;
  const showSuspend = (p.status === 'issued' || p.status === 'active') && canIssue;
  const showResume = p.status === 'suspended' && canIssue;
  const showClose = (p.status === 'active' || p.status === 'suspended' || p.status === 'expired') && canClose;
  if (!showIssue && !showCancel && !showSuspend && !showResume && !showClose) return null;
  return (
    <section aria-label={t('p3pt.act.title')} className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {showResume ? (
          <Button variant="primary" disabled={resume.isPending} onClick={() => resume.mutate()}>
            {t('p3pt.act.resume')}
          </Button>
        ) : null}
        {showSuspend && !suspending ? <Button onClick={() => setSuspending(true)}>{t('p3pt.act.suspend')}</Button> : null}
        {showCancel ? <ConfirmButton label={t('p3pt.act.cancel')} pending={cancel.isPending} onConfirm={() => cancel.mutate()} /> : null}
      </div>
      <ErrorLine error={cancel.error ?? resume.error} />
      {suspending ? (
        <Card
          title={t('p3pt.act.suspend')}
          footer={
            <>
              <Button onClick={() => setSuspending(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" disabled={reason.trim() === '' || suspend.isPending} onClick={() => suspend.mutate()}>
                {t('p3pt.act.suspendConfirm')}
              </Button>
            </>
          }
        >
          <TextField label={t('p3pt.act.reason')} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          <ErrorLine error={suspend.error} />
        </Card>
      ) : null}
      {showIssue ? <IssueForm permit={p} type={type} onDone={done(t('p3pt.act.issued'))} /> : null}
      {showClose ? <CloseForm permit={p} type={type} onDone={done(t('p3pt.act.closed'))} /> : null}
    </section>
  );
}

/** Issue: times, area, conditions, the checklist and the issue sign-offs (issuer and holder on the same screen). */
function IssueForm({ permit: p, type, onDone }: { permit: Permit; type: PermitType | null; onDone: (p: Permit) => void }) {
  const now = new Date();
  const maxHours = type?.maxHours ?? 12;
  const [starts, setStarts] = useState(isoToLocal(now.toISOString()));
  const [ends, setEnds] = useState(isoToLocal(new Date(now.getTime() + Math.min(maxHours, 8) * 3_600_000).toISOString()));
  const [area, setArea] = useState(p.area);
  const [conditions, setConditions] = useState(p.conditions);
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [sigs, setSigs] = useState<Record<string, SignOff | null>>({});
  const roles = unique(['issuer', ...(type?.issueSignOffs ?? [])]);
  const required = new Set((type?.checklist ?? []).filter((i) => i.required).map((i) => i.id));
  const startMs = Date.parse(starts);
  const endMs = Date.parse(ends);
  const timesBad = Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs;
  const tooLong = !timesBad && endMs - startMs > maxHours * 3_600_000;
  const missingTicks = [...required].some((id) => ticks[id] !== true);
  const missingSigs = roles.some((r) => !sigs[r]);
  const issue = useMutation({
    mutationFn: () =>
      api.post(`/permits/${p.id}/issue`, {
        body: PermitIssue.parse({
          startsAt: localToIso(starts),
          endsAt: localToIso(ends),
          area: area.trim(),
          conditions,
          checklist: Object.fromEntries(p.checklist.map((i) => [i.id, ticks[i.id] === true])),
          signatures: roles.map((r) => sigs[r]).filter((s): s is SignOff => !!s),
        }),
        schema: Permit,
      }),
    onSuccess: onDone,
  });
  const errs = fieldErrors(issue.error);
  const valid = !timesBad && !tooLong && area.trim() !== '' && !missingTicks && !missingSigs;
  return (
    <Card
      title={t('p3pt.issue.title')}
      description={t('p3pt.issue.description')}
      footer={
        <Button variant="primary" disabled={!valid || issue.isPending} onClick={() => issue.mutate()}>
          {t('p3pt.issue.submit')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t('p3pt.detail.starts')} type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
          <TextField
            label={t('p3pt.col.ends')}
            type="datetime-local"
            value={ends}
            error={errs['endsAt'] ?? (timesBad ? t('permits.error.times') : tooLong ? t('permits.error.max_hours', { maxHours }) : undefined)}
            onChange={(e) => setEnds(e.target.value)}
          />
        </div>
        <TextField label={t('p3pt.field.area')} value={area} error={errs['area']} onChange={(e) => setArea(e.target.value)} />
        <div>
          <label htmlFor="p3pt-issue-conditions" className="mb-1 block text-sm font-medium text-slate-800">
            {t('p3pt.field.conditions')}
          </label>
          <textarea id="p3pt-issue-conditions" rows={3} value={conditions} onChange={(e) => setConditions(e.target.value)} className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm" />
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('p3pt.detail.checklist')}</legend>
          {errs['checklist'] ? <p className="text-sm text-red-700">{errs['checklist']}</p> : null}
          {p.checklist.map((i) => (
            <CheckboxField
              key={i.id}
              label={required.has(i.id) ? `${i.text} ${t('p3pt.issue.required')}` : i.text}
              checked={ticks[i.id] === true}
              onChange={(e) => setTicks({ ...ticks, [i.id]: e.target.checked })}
            />
          ))}
        </fieldset>
        {errs['signatures'] ? <p className="text-sm text-red-700">{errs['signatures']}</p> : null}
        {roles.map((r) => (
          <SignOffField key={r} role={r} {...(r === 'contractor' ? { defaultName: p.holderName } : {})} onChange={(v) => setSigs((cur) => ({ ...cur, [r]: v }))} />
        ))}
        <ErrorLine error={issue.error} />
      </div>
    </Card>
  );
}

/** Close: the close sign-offs (the holder's close-out signature may already be held from the app). */
function CloseForm({ permit: p, type, onDone }: { permit: Permit; type: PermitType | null; onDone: (p: Permit) => void }) {
  const closeHeld = p.signatures.some((s) => s.stage === 'close' && s.role === 'contractor');
  const roles = unique(['issuer', 'contractor', ...(type?.closeSignOffs ?? [])]).filter((r) => !(r === 'contractor' && closeHeld));
  const [sigs, setSigs] = useState<Record<string, SignOff | null>>({});
  const [notes, setNotes] = useState('');
  const missing = roles.some((r) => !sigs[r]);
  const close = useMutation({
    mutationFn: () => api.post(`/permits/${p.id}/close`, { body: PermitClose.parse({ signatures: roles.map((r) => sigs[r]).filter((s): s is SignOff => !!s), notes }), schema: Permit }),
    onSuccess: onDone,
  });
  return (
    <Card
      title={t('p3pt.close.title')}
      description={closeHeld ? t('p3pt.close.held') : t('p3pt.close.description')}
      footer={
        <Button variant="primary" disabled={missing || close.isPending} onClick={() => close.mutate()}>
          {t('p3pt.close.submit')}
        </Button>
      }
    >
      <div className="space-y-4">
        {roles.map((r) => (
          <SignOffField key={r} role={r} {...(r === 'contractor' ? { defaultName: p.holderName } : {})} onChange={(v) => setSigs((cur) => ({ ...cur, [r]: v }))} />
        ))}
        <div>
          <label htmlFor="p3pt-close-notes" className="mb-1 block text-sm font-medium text-slate-800">
            {t('p3pt.close.notes')}
          </label>
          <textarea id="p3pt-close-notes" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm" />
        </div>
        <ErrorLine error={close.error} />
      </div>
    </Card>
  );
}
